import * as THREE from "three";
import { asOverheadUI } from "../overheadLayer.js";
import { U } from "../streetScene.js";
import { kaponHeightAt } from "../kaponTerrain.js";
import { dayNight } from "../dayNight.js";
import { HOG_DIRS, PET_SIZE_PER_LEVEL, type PetHog } from "@ghostland/shared";
import { assetUrl } from "../../assetVer.js";

// Load through a private LoadingManager so pet sheet downloads don't drive the boot/scene veil
// (same reasoning as the hog controller).
const ENTITY_MGR = new THREE.LoadingManager();
ENTITY_MGR.setURLModifier((url) => assetUrl(url));

/**
 * Renders the summoned PET hogs (mini companions) as billboard sprites. Position + state + facing come
 * from the synced `PetHog` schema (server-driven follow AI). Unlike wild hogs these are small, never
 * attack, have no aggro alert, and follow their owner across EVERY scene — so visibility is decided
 * per-pet by the caller (the owner always sees their pet; others only when in the same zone).
 *
 * The pet reuses the wild-hog sprite sheets (idle + walk) at a smaller scale. Any state whose sheet is
 * missing falls back to "idle", so the controller is robust before any variant art ships.
 */

const CELL = 103;        // pet sheets share one square cell = union over its 4 v7 states (was 68 for v1 art)
const ROWS = 8;
const LERP = 0.22;
const SCALE = 1.157; // world units tall — a "mini" hog (vs the wild hog's 2.14); preserves the prior mini size on the 103 cell
const FOOT = 0.158;      // pet v7 foot anchor (art sits ~16px above the 103 cell bottom)
const HIT_MS = 260;
const FADE_MS = 380;     // opacity 0→1 fade-in after a scene-change snap (matches the char billboard)
const WARP2 = 8 * 8;     // world units²: a server position jump beyond this = scene change → snap + fade

/** Frames per state (matches /assets/mobs/pet_hog/manifest.json). charge (wind-up) + attack loop while engaged. */
const FRAMES: Record<string, number> = { idle: 17, walk: 9, charge: 9, attack: 8 };
const STATE_MS: Record<string, number> = { idle: 150, walk: 120, charge: 110, attack: 80 };
const STATES = ["idle", "walk", "charge", "attack"];

interface PetEntity {
  sprite: THREE.Sprite;
  texByState: Map<string, THREE.Texture>;
  bar: THREE.Sprite; barCanvas: HTMLCanvasElement; barTex: THREE.CanvasTexture; lastHp: number;
  ownerSid: string;
  x: number; z: number; serverX: number; serverZ: number;
  dir: string; state: string; health: number; maxHealth: number; level: number;
  phase: number; clock: number; hitClock: number;
  warp: boolean;       // set when the server position jumped far (scene change) → snap + fade next tick
  fadeClock: number;   // remaining fade-in time (ms); >0 = ramping opacity 0→1
}

/** Sprite scale for a pet level — grows with level (a higher-level hoglet is visibly bigger). */
function scaleFor(level: number): number {
  return SCALE * (1 + Math.max(0, level - 1) * PET_SIZE_PER_LEVEL);
}

export class PetHogController {
  private readonly pets = new Map<string, PetEntity>();
  private readonly base = new Map<string, THREE.Texture>();

  constructor(private readonly scene: THREE.Scene) {
    const loader = new THREE.TextureLoader(ENTITY_MGR);
    for (const st of STATES) {
      // onError → leave it unloaded; applyState falls back to "idle". Avoids hard failures if a
      // variant sheet hasn't shipped yet.
      const t = loader.load(`/assets/mobs/pet_hog/${st}.png`, undefined, undefined, () => { this.base.delete(st); });
      t.magFilter = t.minFilter = THREE.NearestFilter; t.colorSpace = THREE.SRGBColorSpace;
      this.base.set(st, t);
    }
  }

  add(key: string, pet: PetHog): void {
    if (this.pets.has(key)) { this.sync(key, pet); return; }
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ transparent: true, alphaTest: 0.02 }));
    const sc = scaleFor(pet.level);
    sprite.center.set(0.5, FOOT); sprite.scale.set(sc, sc, 1); sprite.visible = false;
    (sprite.material as THREE.SpriteMaterial).opacity = 0; // fade in on first appearance (summon)
    sprite.position.set(pet.x * U, 0.02, pet.y * U);
    this.scene.add(sprite);

    const barCanvas = document.createElement("canvas"); barCanvas.width = 40; barCanvas.height = 6;
    const barTex = new THREE.CanvasTexture(barCanvas); barTex.magFilter = THREE.NearestFilter; barTex.minFilter = THREE.NearestFilter;
    const bar = new THREE.Sprite(new THREE.SpriteMaterial({ map: barTex, transparent: true, depthTest: false }));
    bar.center.set(0.5, 0); bar.scale.set(0.5, 0.075, 1); bar.renderOrder = 999; bar.visible = false;
    asOverheadUI(bar); // crisp overlay layer — never softened by the world depth-of-field
    this.scene.add(bar);

    const e: PetEntity = {
      sprite, texByState: new Map(), bar, barCanvas, barTex, lastHp: -1, ownerSid: pet.ownerSid,
      x: pet.x * U, z: pet.y * U, serverX: pet.x * U, serverZ: pet.y * U,
      dir: pet.dir, state: pet.state, health: pet.health, maxHealth: pet.maxHealth, level: pet.level,
      phase: 0, clock: 0, hitClock: 0, warp: false, fadeClock: FADE_MS,
    };
    this.pets.set(key, e);
    this.applyState(e, pet.state);
  }

  sync(key: string, pet: PetHog): void {
    const e = this.pets.get(key); if (!e) return;
    const nx = pet.x * U, nz = pet.y * U;
    const jx = nx - e.serverX, jz = nz - e.serverZ;
    if (jx * jx + jz * jz > WARP2) e.warp = true; // server snapped it to a new scene → snap + fade, don't streak
    e.serverX = nx; e.serverZ = nz; e.ownerSid = pet.ownerSid;
    e.dir = pet.dir; e.health = pet.health; e.maxHealth = pet.maxHealth;
    if (pet.level !== e.level) { e.level = pet.level; const sc = scaleFor(e.level); e.sprite.scale.set(sc, sc, 1); }
    if (pet.state !== e.state) this.applyState(e, pet.state);
  }

  /** The owner's sessionId for a pet (so the caller can decide own-vs-other visibility). */
  ownerOf(key: string): string | null { return this.pets.get(key)?.ownerSid ?? null; }

  /** Raycast-pick a visible pet sprite. Returns its key or null. */
  pick(ray: THREE.Raycaster): string | null {
    const vis = [...this.pets.entries()].filter(([, e]) => e.sprite.visible);
    const hits = ray.intersectObjects(vis.map(([, e]) => e.sprite), false);
    if (!hits.length) return null;
    const obj = hits[0].object;
    for (const [key, e] of vis) if (e.sprite === obj) return key;
    return null;
  }

  /** Visible pets within `range` world units of (wx,wz), nearest first — for Tab targeting. */
  inRange(wx: number, wz: number, range: number): { key: string; x: number; z: number }[] {
    const r2 = range * range, out: { key: string; x: number; z: number }[] = [];
    for (const [key, e] of this.pets) {
      if (!e.sprite.visible) continue;
      const dx = e.serverX - wx, dz = e.serverZ - wz;
      if (dx * dx + dz * dz <= r2) out.push({ key, x: e.serverX, z: e.serverZ });
    }
    out.sort((a, b) => (a.x - wx) ** 2 + (a.z - wz) ** 2 - ((b.x - wx) ** 2 + (b.z - wz) ** 2));
    return out;
  }

  posOf(key: string): { x: number; z: number } | null {
    const e = this.pets.get(key); return e ? { x: e.x, z: e.z } : null;
  }

  infoOf(key: string): { health: number; maxHealth: number } | null {
    const e = this.pets.get(key); return e ? { health: e.health, maxHealth: e.maxHealth } : null;
  }

  hit(key: string): void { const e = this.pets.get(key); if (e) e.hitClock = HIT_MS; }

  remove(key: string): void {
    const e = this.pets.get(key); if (!e) return;
    this.scene.remove(e.sprite); (e.sprite.material as THREE.SpriteMaterial).dispose();
    for (const t of e.texByState.values()) t.dispose();
    this.scene.remove(e.bar); (e.bar.material as THREE.SpriteMaterial).dispose(); e.barTex.dispose();
    this.pets.delete(key);
  }

  private applyState(e: PetEntity, state: string): void {
    const st = this.base.has(state) ? state : "idle"; // fall back if a variant sheet is missing
    e.state = st; e.phase = 0; e.clock = 0;
    let tex = e.texByState.get(st);
    if (!tex) {
      const baseTex = this.base.get(st); if (!baseTex) return;
      tex = baseTex.clone(); tex.needsUpdate = true;
      tex.repeat.set(1 / (FRAMES[st] ?? 1), 1 / ROWS);
      e.texByState.set(st, tex);
    }
    (e.sprite.material as THREE.SpriteMaterial).map = tex;
    (e.sprite.material as THREE.SpriteMaterial).needsUpdate = true;
  }

  private redrawBar(e: PetEntity): void {
    const ctx = e.barCanvas.getContext("2d")!;
    const frac = e.maxHealth > 0 ? Math.max(0, Math.min(1, e.health / e.maxHealth)) : 0;
    ctx.clearRect(0, 0, 40, 6);
    ctx.fillStyle = "#1c1208"; ctx.fillRect(0, 0, 40, 6);
    ctx.fillStyle = frac > 0.5 ? "#6ad06a" : frac > 0.25 ? "#e0c34a" : "#d05a4a";
    ctx.fillRect(1, 1, Math.round(38 * frac), 4);
    e.barTex.needsUpdate = true;
  }

  /** `isVisible(ownerSid, serverPxX, serverPxY)` decides per-pet visibility (owner everywhere; others
   *  only in the same zone). `camYaw` orients the directional sprite row like the player billboard. */
  tick(dt: number, camYaw: number, isVisible: (ownerSid: string, sx: number, sy: number) => boolean): void {
    for (const e of this.pets.values()) {
      const show = isVisible(e.ownerSid, e.serverX / U, e.serverZ / U);
      e.sprite.visible = show;
      if (!show) { e.bar.visible = false; continue; }
      const mat = e.sprite.material as THREE.SpriteMaterial;
      if (e.warp) {
        // Scene change: snap to the destination (don't streak across) and fade back in.
        e.x = e.serverX; e.z = e.serverZ; e.warp = false; e.fadeClock = FADE_MS;
      } else {
        e.x += (e.serverX - e.x) * LERP;
        e.z += (e.serverZ - e.z) * LERP;
      }
      const gy = kaponHeightAt(e.x, e.z); // 0 outside the Kapon rect

      // Opacity fade-in (after a scene-change snap / first appearance) — alphaTest dropped to 0 while
      // fading so the alpha-blend is smooth, restored to 0.5 (crisp pixel edges) when it finishes.
      if (e.fadeClock > 0) {
        e.fadeClock = Math.max(0, e.fadeClock - dt * 1000);
        mat.opacity = 1 - e.fadeClock / FADE_MS; mat.alphaTest = 0.02; // tiny (not 0) so transparent texels stay discarded → no green-ground box
        if (e.fadeClock === 0) { mat.opacity = 1; mat.alphaTest = 0.5; }
      }

      let shake = 0;
      if (e.hitClock > 0) {
        e.hitClock = Math.max(0, e.hitClock - dt * 1000);
        const t = e.hitClock / HIT_MS;
        mat.color.setRGB(1, 1 - 0.72 * t, 1 - 0.72 * t);
        shake = Math.sin(e.hitClock * 0.07) * 0.1 * t;
        if (e.hitClock === 0) dayNight.lightAt(e.x, e.z, mat.color);
      } else {
        dayNight.lightAt(e.x, e.z, mat.color);
      }
      e.sprite.position.set(e.x + shake, 0.02 + gy, e.z);

      const n = FRAMES[e.state] ?? 1, ms = STATE_MS[e.state] ?? 150;
      e.clock += dt * 1000;
      while (e.clock >= ms) { e.clock -= ms; e.phase = (e.phase + 1) % n; }

      const idx = Math.max(0, HOG_DIRS.indexOf(e.dir as (typeof HOG_DIRS)[number]));
      const angle = idx * (Math.PI / 4);
      const row = Math.round(((((angle - camYaw) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2)) / (Math.PI / 4)) % ROWS;
      const tex = e.texByState.get(e.state);
      if (tex) tex.offset.set(e.phase / n, 1 - (row + 1) / ROWS);

      if (e.health !== e.lastHp) { this.redrawBar(e); e.lastHp = e.health; }
      e.bar.visible = e.health < e.maxHealth;
      if (e.bar.visible) e.bar.position.set(e.x, gy + SCALE * 0.96, e.z);
    }
  }

  dispose(): void {
    for (const key of [...this.pets.keys()]) this.remove(key);
    for (const t of this.base.values()) t.dispose();
  }
}
